import type { QueryClient } from '@tanstack/react-query';

import type { FeedItem, IssueHolding, LandingRecord, LandingStartResult, LandingTakeOverResult } from '../../types/domain.types';
import { landingQueryOptions, myFeedQueryOptions } from '../../utils/queryOptions';

const heldBy = <T extends Pick<FeedItem, 'startedBy' | 'status' | 'nextAction'>>(row: T, holding: IssueHolding): T => ({
  ...row,
  startedBy: holding.startedBy ?? row.startedBy,
  status: holding.status ?? row.status,
  nextAction: holding.nextAction,
});

export const markIssueStarted = async (queryClient: QueryClient, issueId: string, holding: IssueHolding): Promise<void> => {
  const { queryKey } = myFeedQueryOptions();
  await queryClient.cancelQueries({ queryKey });
  queryClient.setQueryData(queryKey, (page) =>
    page === undefined
      ? page
      : { ...page, items: page.items.map((item): FeedItem => (item.issueId === issueId ? heldBy(item, holding) : item)) },
  );
};

export const refreshAfterStart = (queryClient: QueryClient): void => {
  void queryClient.invalidateQueries({ queryKey: myFeedQueryOptions().queryKey });
};

type LandingOutcome = LandingStartResult | LandingTakeOverResult;

const heldRecord = (record: LandingRecord, holding: IssueHolding): LandingRecord => ({
  ...record,
  startedBy: holding.startedBy ?? record.startedBy,
  status: holding.status ?? record.status,
  nextAction: holding.nextAction,
});

const recordAfter = (record: LandingRecord, outcome: LandingOutcome): LandingRecord => {
  switch (outcome.kind) {
    case 'STARTED':
      return {
        ...heldRecord(record, outcome),
        hooter: outcome.hooterSilenced && record.hooter !== null ? { ...record.hooter, sounding: false } : record.hooter,
      };
    case 'TAKEN_OVER':
    case 'ALREADY_YOURS':
    case 'ALREADY_STARTED':
      return heldRecord(record, outcome);
    case 'NOT_STARTED':
    case 'NOT_OPEN':
      return record;
  }
};

export const applyLandingOutcome = async (queryClient: QueryClient, token: string, outcome: LandingOutcome): Promise<void> => {
  const { queryKey } = landingQueryOptions(token);
  await queryClient.cancelQueries({ queryKey });
  queryClient.setQueryData(queryKey, (record) => (record === undefined || record === null ? record : recordAfter(record, outcome)));
};

export const isStaleAfter = (outcome: LandingOutcome | undefined): boolean =>
  outcome?.kind === 'NOT_OPEN' || outcome?.kind === 'NOT_STARTED' || (outcome?.kind === 'ALREADY_STARTED' && outcome.startedBy === null);

export const refreshLanding = (queryClient: QueryClient, token: string, reread: boolean): void => {
  if (reread) void queryClient.invalidateQueries({ queryKey: landingQueryOptions(token).queryKey });
  void queryClient.invalidateQueries({ queryKey: myFeedQueryOptions().queryKey });
};
